import type { Axios } from "axios";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useAxios } from "@packsize/authentication";

import { getPackagingDesigns } from "@/api/packaging-design-api";
import _0100 from "@/assets/images/packaging-design/carton-0100-erected.svg";
import _0200 from "@/assets/images/packaging-design/carton-0200-erected.svg";
import _0201 from "@/assets/images/packaging-design/carton-0201-erected.svg";
import _0202 from "@/assets/images/packaging-design/carton-0202-erected.svg";
import _0203 from "@/assets/images/packaging-design/carton-0203-erected.svg";
import _0301 from "@/assets/images/packaging-design/carton-0301-erected.svg";
import _0331 from "@/assets/images/packaging-design/carton-0331-erected.svg";
import _0401 from "@/assets/images/packaging-design/carton-0401-erected.svg";
import _0403 from "@/assets/images/packaging-design/carton-0403-erected.svg";
import _0410 from "@/assets/images/packaging-design/carton-0410-erected.svg";
import _0412 from "@/assets/images/packaging-design/carton-0412-erected.svg";
import _0421 from "@/assets/images/packaging-design/carton-0421-erected.svg";
import noImage from "@/assets/images/packaging-design/custom-erected.png";
import type { PackagingDesign } from "@/schemas";

import { type SavedJobPackagingDesign } from "../types/dependencies";

const transformPackagingDesign = (packagingDesign: PackagingDesign): SavedJobPackagingDesign => {
	const defaultDesign: SavedJobPackagingDesign = {
		...packagingDesign,
		designName: packagingDesign.name,
		image2d: "",
		image3d: "",
		length: 0,
		width: 0,
		height: 0,
	};
	const boxStyle = defaultDesign.designId.toString().substring(0, 3);

	switch (boxStyle) {
		case "110":
			return {
				...defaultDesign,
				image3d: _0100,
				length: 20,
				width: 10,
				height: 13,
				X1: 5,
			};

		case "200":
			return {
				...defaultDesign,
				image3d: _0200,
				length: 20,
				width: 10,
				height: 13,
				X1: 5,
			};

		case "201":
			return {
				...defaultDesign,
				image3d: _0201,
				length: 20,
				width: 10,
				height: 13,
				X1: 5,
			};

		case "202":
			return {
				...defaultDesign,
				image3d: _0202,
				length: 10,
				width: 10,
				height: 10,
				X1: 7,
				X2: 5,
			};

		case "203":
			return {
				...defaultDesign,
				image3d: _0203,
				length: 20,
				width: 8,
				height: 12,
				X1: 5,
			};

		case "301":
		case "452":
		case "453":
			return {
				...defaultDesign,
				image3d: _0301,
				length: 20,
				width: 12,
				height: 5,
				X1: 5,
			};
		case "331":
		case "454":
		case "455":
			return {
				...defaultDesign,
				image3d: _0331,
				length: 20,
				width: 8,
				height: 12,
				X1: 5,
			};
		case "401":
			return {
				...defaultDesign,
				image3d: _0401,
				length: 20,
				width: 15,
				height: 5,
				X1: 3,
			};
		case "403":
			return {
				...defaultDesign,
				image3d: _0403,
				length: 20,
				width: 15,
				height: 5,
				X1: 3,
			};
		case "410":
			return {
				...defaultDesign,
				image3d: _0410,
				length: 20,
				width: 15,
				height: 5,
				X1: 3,
			};
		case "412":
			return {
				...defaultDesign,
				image3d: _0412,
				length: 20,
				width: 15,
				height: 5,
				X1: 3,
			};
		case "470":
		case "471":
		case "472":
			return {
				...defaultDesign,
				image3d: _0421,
				length: 20,
				width: 15,
				height: 5,
				X1: 3,
			};
		default:
			return {
				...defaultDesign,
				image3d: noImage,
				length: 10,
				width: 10,
				height: 10,
			};
	}
};

export const PACKAGING_SOLUTION_PACKAGING_DESIGNS_QUERY_KEY = "packaging-solution-packaging-designs" as const;

const getPackagingSolutionPackagingDesigns = async (client: Axios): Promise<SavedJobPackagingDesign[]> => {
	const designs = await getPackagingDesigns(client);
	return designs.map(transformPackagingDesign);
};

export const usePackagingSolutionPackagingDesigns = ({ queryConfig = {} } = {}) => {
	const client = useAxios();
	const query = useQuery({
		...queryOptions({
			queryKey: [client, PACKAGING_SOLUTION_PACKAGING_DESIGNS_QUERY_KEY],
			queryFn: () => getPackagingSolutionPackagingDesigns(client),
		}),
		...queryConfig,
	});

	return {
		packagingDesigns: query.data ?? [],
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
		refetch: query.refetch,
	};
};
